import { toast } from "sonner";
import { saveAs } from 'file-saver';
import { enhanceElementsForExport, restoreOriginalStyles } from './pdf-style-enhancer';
import { generateCanvas, CanvasProgressCallback } from './canvas-generator';

/**
 * Progress callback type for PNG saving
 */
export type PNGProgressCallback = (stage: string, progress?: number) => void;

/**
 * Exports the journey map as a PNG image with progressive feedback
 * Captures the map with light theme for consistent exports
 * Maintains landscape-friendly layout for maps with multiple stages
 */
export const exportJourneyAsPNG = async (
  canvasRef: React.RefObject<HTMLDivElement>, 
  zoomLevel: number, 
  setZoomLevel: (zoom: number) => void,
  mapName?: string
) => {
  if (!canvasRef.current) {
    toast.error("Cannot export: No journey map found");
    return;
  }
  
  // Check if map is empty
  const stageCards = canvasRef.current.querySelectorAll('.stage-card');
  if (stageCards.length === 0) {
    toast.error("Cannot export: Journey map is empty");
    return;
  }
  
  // Create a toast ID for updating the same toast with progress
  const toastId = "png-export-progress";
  
  // Store current container reference and create backup copy
  const containerElement = canvasRef.current;
  let originalStyles: any[] = [];
  
  try {
    // Step 1: Initialize export with theme info
    const exportMsg = "Preparing image export with light theme...";
    toast.loading(exportMsg, { id: toastId });
    
    // Step 2: Enhance styles for export - enforcing light theme and appropriate layout
    toast.loading("Enhancing visuals for export...", { id: toastId });
    originalStyles = enhanceElementsForExport(containerElement);
    
    // Create progress callback for canvas generation
    const canvasProgressCallback: CanvasProgressCallback = (stage, progress) => {
      const stageMessages: Record<string, string> = {
        setup: "Setting up canvas...",
        rendering: "Capturing journey map...",
        enhancing: "Processing image...",
        finalizing: "Finalizing image...",
        complete: "Image capture complete"
      };
      
      const message = stageMessages[stage] || `Processing (${progress || 0}%)...`;
      toast.loading(message, { id: toastId });
    };
    
    // Step 3: Generate canvas with progress updates
    const canvas = await generateCanvas(containerElement, originalStyles, canvasProgressCallback);
    
    // Step 4: Save the PNG image
    toast.loading("Saving image file...", { id: toastId });
    
    // Sanitize map name for filename
    const sanitizedMapName = mapName && mapName.trim() ? 
      mapName.trim().replace(/[^a-zA-Z0-9\s-_]/g, '_') : 
      'journey_map';
    
    // Save the PNG image with the sanitized name
    await saveImageAsPNG(canvas, sanitizedMapName);
    
    toast.success("Image saved successfully!", { id: toastId });
  } catch (error) {
    console.error("Export error:", error);
    const errorMessage = error instanceof Error ? 
      `Failed to export image: ${error.message}` : 
      "Failed to export image";
    toast.error(errorMessage, { id: toastId });
  } finally {
    // Restore styles immediately but in a try-catch to handle any issues
    try {
      if (originalStyles.length > 0) {
        restoreOriginalStyles(originalStyles);
      }
    } catch (styleError) {
      console.error("Error restoring styles:", styleError);
    }
  }
};

/**
 * Saves the canvas as a PNG image
 * Returns a promise that resolves when the save is complete
 */
const saveImageAsPNG = (canvas: HTMLCanvasElement, mapName: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    try {
      // Validate canvas
      if (!canvas || canvas.width <= 0 || canvas.height <= 0) {
        reject(new Error("Invalid canvas: Canvas is empty or has invalid dimensions"));
        return;
      }
      
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error("Failed to generate image blob"));
          return;
        }
        
        // Generate timestamp for filename
        const timestamp = new Date().toISOString().split('T')[0];
        const filename = `${mapName}_${timestamp}.png`;
        
        saveAs(blob, filename);
        resolve();
      }, 'image/png', 1.0);
    } catch (error) {
      console.error("PNG saving error:", error);
      reject(error);
    }
  });
};
